import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PenTool,
  ArrowRight,
  User,
  Phone,
  FileText,
  Upload,
  X,
  CheckCircle,
  Loader,
  AlertCircle,
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GuestNotaryService() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  
  const [form, setForm] = useState({
    client_name: '',
    phone: '',
    subject: '',
    description: '',
    client_requests: '',
  });

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // التحقق من عدد الملفات (الحد الأقصى 5)
    if (files.length + selectedFiles.length > 5) {
      setError('يمكنك رفع 5 ملفات كحد أقصى');
      return;
    }
    
    // التحقق من حجم الملفات (الحد الأقصى 10MB لكل ملف)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = selectedFiles.filter(file => {
      if (file.size > maxSize) {
        setError(`الملف ${file.name} أكبر من 10MB`);
        return false;
      }
      return true;
    });
    
    setFiles([...files, ...validFiles]);
    setError('');
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // التحقق من البيانات المطلوبة
    if (!form.client_name || !form.phone || !form.subject || !form.description) {
      setError('يرجى ملء جميع الحقول المطلوبة');
      setLoading(false);
      return;
    }

    try {
      // إنشاء FormData لرفع الملفات
      const formData = new FormData();
      formData.append('client_name', form.client_name);
      formData.append('phone', form.phone);
      formData.append('subject', form.subject);
      formData.append('description', form.description);
      formData.append('client_requests', form.client_requests);
      formData.append('request_type', 'notary');
      formData.append('service_type', 'خدمات الموثق');
      
      // إضافة الملفات
      files.forEach((file, index) => {
        formData.append(`file_${index}`, file);
      });

      await axios.post(`${API}/guest-notary-request`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(true);
    } catch (err) {
      console.error('Error submitting request:', err);
      setError(err.response?.data?.detail || 'حدث خطأ أثناء إرسال الطلب');
    }
    setLoading(false);
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📊';
    if (['zip', 'rar'].includes(ext)) return '📦';
    return '📎';
  };

  if (success) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4" dir="rtl" lang="ar">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-primary mb-4">
            تم إرسال طلبك بنجاح!
          </h2>
          <p className="text-gray-600 font-body mb-6">
            سيتم التواصل معك قريباً من قبل فريقنا المتخصص.
            <br />
            شكراً لثقتكم بمكتب المحامي هشام يوسف الخياط.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all font-body"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      {/* Header */}
      <header className="bg-primary py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors font-body"
          >
            <ArrowRight className="w-5 h-5" />
            العودة للرئيسية
          </button>
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="text-white font-heading font-semibold hidden sm:block">
              مكتب المحامي هشام يوسف الخياط
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-l from-purple-600 to-purple-700 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <PenTool className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold">خدمات الموثق</h1>
                <p className="text-purple-100 font-body">توثيق العقود والوكالات والإقرارات</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 font-body">{error}</span>
              </div>
            )}

            {/* اسم العميل */}
            <div>
              <label className="block text-lg font-heading font-semibold text-primary mb-2">
                اسم العميل <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  data-testid="notary-client-name"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder="أدخل اسمك الكامل"
                  className="w-full pr-12 pl-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-body"
                  required
                />
              </div>
            </div>

            {/* رقم الجوال */}
            <div>
              <label className="block text-lg font-heading font-semibold text-primary mb-2">
                رقم الجوال <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  data-testid="notary-phone"
                  value={form.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setForm({ ...form, phone: val });
                  }}
                  placeholder="05xxxxxxxx"
                  maxLength={10}
                  className="w-full pr-12 pl-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-body"
                  required
                />
              </div>
              <p className="text-sm text-gray-500 mt-1 font-body">
                {form.phone.length}/10 أرقام
                {form.phone.length === 10 && <span className="text-green-600 mr-2">✓</span>}
              </p>
            </div>

            {/* موضوع الاستشارة */}
            <div>
              <label className="block text-lg font-heading font-semibold text-primary mb-2">
                موضوع الاستشارة <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute right-4 top-4 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  data-testid="notary-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="مثال: توثيق عقد بيع عقار"
                  className="w-full pr-12 pl-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-body"
                  required
                />
              </div>
            </div>

            {/* شرح الاستشارة */}
            <div>
              <label className="block text-lg font-heading font-semibold text-primary mb-2">
                شرح الاستشارة <span className="text-red-500">*</span>
              </label>
              <textarea
                data-testid="notary-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="اشرح تفاصيل طلبك بشكل واضح..."
                rows={4}
                className="w-full px-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-body resize-none"
                required
              />
            </div>

            {/* طلبات العميل */}
            <div>
              <label className="block text-lg font-heading font-semibold text-primary mb-2">
                طلبات العميل
              </label>
              <textarea
                data-testid="notary-requests"
                value={form.client_requests}
                onChange={(e) => setForm({ ...form, client_requests: e.target.value })}
                placeholder="ما هي طلباتك المحددة من هذه الخدمة؟"
                rows={3}
                className="w-full px-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-body resize-none"
              />
            </div>

            {/* رفع الملفات */}
            <div>
              <label className="block text-lg font-heading font-semibold text-primary mb-2">
                المرفقات (اختياري - حد أقصى 5 ملفات)
              </label>
              <p className="text-gray-500 text-sm mb-3 font-body">
                يمكنك رفع مستندات بصيغ: PDF, Word, Excel, صور (JPG, PNG) - الحد الأقصى 10MB لكل ملف
              </p>
              
              {/* منطقة رفع الملفات */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  data-testid="notary-files"
                  onChange={handleFileChange}
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar"
                  className="hidden"
                  disabled={files.length >= 5}
                />
                <label
                  htmlFor="file-upload"
                  className={`cursor-pointer ${files.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-body">
                    {files.length >= 5 
                      ? 'تم الوصول للحد الأقصى من الملفات'
                      : 'اضغط هنا لاختيار الملفات أو اسحبها وأفلتها'
                    }
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {files.length}/5 ملفات
                  </p>
                </label>
              </div>

              {/* قائمة الملفات المرفوعة */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getFileIcon(file.name)}</span>
                        <div>
                          <p className="font-body text-sm text-gray-700 truncate max-w-[200px] sm:max-w-[300px]">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-red-100 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* زر الإرسال */}
            <button
              type="submit"
              disabled={loading}
              data-testid="notary-submit"
              className="w-full py-4 bg-purple-600 text-white text-xl font-bold rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  جاري الإرسال...
                </>
              ) : (
                <>
                  <PenTool className="w-6 h-6" />
                  إرسال الطلب
                </>
              )}
            </button>
          </form>
        </div>

        {/* معلومات التواصل */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 font-body">
            للاستفسارات العاجلة، تواصل معنا عبر الواتساب:
            <a
              href="https://wa.me/966568228974"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 font-bold mr-2 hover:underline"
            >
              +966 56 822 8974
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
